/**
 * The Approvals board's requests, read from the platform's approval chain (build 4).
 *
 * Renders nothing; sits in the shell. On the platform, the moment the engine
 * is here it is told the chain is the platform's — the sample and the acting
 * role picker are put away before the board can draw them — and the requests
 * are installed as they arrive, each carrying whether the person signed in may
 * sign the step it waits on, and if not, why.
 *
 * Under the mock it reads nothing and leaves the sample, the picker and the
 * in-memory chain exactly as they were.
 *
 * Like CertificatesFeed it costs nothing until a board is open: its read waits
 * for the engine, and it never causes the engine to load.
 */
import { useEffect, useRef, useState } from "react";
import { useSession } from "../../../app/SessionContext";
import { DataSourceError } from "../../../api/dataSource";
import { useApprovalsBoard } from "../../../api/approvalsQueries";
import { demoIfLoaded, whenDemoLoaded } from "../../domain/load";

/** The merge and the tables it reads, fetched only when there is something to merge. */
const merger = () => import("../mergeApprovals");

export function ApprovalsFeed() {
  const { source, site, principal } = useSession();
  const live = source.kind === "platform";
  const [engine, setEngine] = useState(() => demoIfLoaded() !== null);

  /* synchronously on arrival: the board must not draw the sample on the live site even once */
  useEffect(() => {
    let gone = false;
    whenDemoLoaded((d) => {
      if (gone) return;
      if (live) d.apprGoLive(false, principal.name);
      setEngine(true);
    });
    return () => {
      gone = true;
    };
  }, [live, principal.name]);

  /* another site's requests never stand in while this one's load */
  const siteRef = useRef(site.id);
  useEffect(() => {
    if (siteRef.current === site.id) return;
    siteRef.current = site.id;
    if (live) demoIfLoaded()?.apprGoLive(true, principal.name);
  }, [live, site.id, principal.name]);

  const board = useApprovalsBoard({ enabled: live && engine });
  const data = board.data;
  const error = board.error;

  useEffect(() => {
    if (!live || !engine) return;
    if (error) {
      const why = error instanceof Error ? error.message : String(error);
      const refused = error instanceof DataSourceError && (error.status === 401 || error.status === 403);
      void merger().then((m) => (refused ? m.mergeApprovals(null, principal.name, why) : m.approvalsUnread(why)));
      return;
    }
    if (data) void merger().then((m) => m.mergeApprovals(data, principal.name));
  }, [live, engine, data, error, principal.name]);

  return null;
}
